import { z } from "zod";

import {
  GenerationMessageSchema,
  SeedsMapSchema,
  type SeedsMapInput,
} from "@/types/creativesphere";

// ── Core models ───────────────────────────────────────────────────────────────

export const PropagationItemSchema = z.object({
  slug: z.string().optional(),
  id: z.string().optional(),
  propagation_slug: z.string().optional(),
  is_accepted: z.boolean().default(true),
  generation_id: z.string().default(""),
  // Loose string: a legacy/unknown value must not reject the item.
  status: z.string().optional(),
  generation: GenerationMessageSchema.nullish(),
  vendor_product_id: z.string().optional(),
  vendor_variant_id: z.string().optional(),
  source_image_url: z.string().optional(),
  product_title: z.string().optional(),
  product_description: z.string().optional(),
  product_category: z.string().optional(),
});
export type PropagationItem = z.infer<typeof PropagationItemSchema>;

export interface PropagationItemInput {
  vendor_product_id?: string;
  vendor_variant_id?: string;
  source_image_url?: string;
  product_title?: string;
  is_accepted?: boolean;
}

/** proto `ProductGroupType` — how a propagation's target group is identified. */
export const PRODUCT_GROUP_TYPE = {
  UNSPECIFIED: "PRODUCT_GROUP_TYPE_UNSPECIFIED",
  COLLECTION: "PRODUCT_GROUP_TYPE_COLLECTION",
  TAG: "PRODUCT_GROUP_TYPE_TAG",
  CATEGORY: "PRODUCT_GROUP_TYPE_CATEGORY",
} as const;
export type ProductGroupType =
  (typeof PRODUCT_GROUP_TYPE)[keyof typeof PRODUCT_GROUP_TYPE];

export const ProductGroupTypeSchema = z.enum([
  PRODUCT_GROUP_TYPE.UNSPECIFIED,
  PRODUCT_GROUP_TYPE.COLLECTION,
  PRODUCT_GROUP_TYPE.TAG,
  PRODUCT_GROUP_TYPE.CATEGORY,
]);

/** The full propagation workflow state. */
export const PropagationSchema = z.object({
  slug: z.string(),
  /** Display name set at creation ("<group handle> · <blueprint name>"). */
  name: z.string().optional(),
  site_id: z.string().default(""),
  status: z.string().default("UNSPECIFIED"),
  propagation_state: z.string().default("UNSPECIFIED"),
  processing_state: z.string().optional(),
  step: z.string().optional(),
  started_from: z.string().optional(),
  from_blueprint_id: z.string().optional(),
  group_id: z.string().optional(),
  group_type: ProductGroupTypeSchema.optional(),
  seeds_map: SeedsMapSchema.default({}),
  additional_prompt: z.string().optional(),
  pilot_vendor_product_id: z.string().optional(),
  pilot_vendor_variant_id: z.string().optional(),
  pilot_image_url: z.string().optional(),
  pilot_additional_prompt: z.string().optional(),
  items: z.array(PropagationItemSchema).default([]),
  created_by: z.string().optional(),
  created_at: z.string().optional(),
  updated_at: z.string().optional(),
  blueprint_id: z.string().optional(),
});
export type Propagation = z.infer<typeof PropagationSchema>;

/**
 * `propagation_state` values (proto enum PropagationState). Kept as a const map
 * rather than a Zod enum because the field stays a loose string — an unknown
 * backend value must not reject the whole propagation.
 */
export const PROPAGATION_STATE = {
  UNSPECIFIED: "PROPAGATION_STATE_UNSPECIFIED",
  SETUP: "PROPAGATION_STATE_SETUP",
  PILOT_GENERATING: "PROPAGATION_STATE_PILOT_GENERATING",
  PILOT_READY: "PROPAGATION_STATE_PILOT_READY",
  PILOT_ACCEPTED: "PROPAGATION_STATE_ACCEPT_PILOT",
  PROPAGATING: "PROPAGATION_STATE_PROPAGATE_COLLECTION",
  COLLECTION_REVIEW: "PROPAGATION_STATE_COLLECTION_REVIEW",
  COLLECTION_ACCEPTED: "PROPAGATION_STATE_COLLECTION_ACCEPTED",
  PUBLISHED: "PROPAGATION_STATE_PUBLISH",
  SCHEDULED: "PROPAGATION_STATE_SCHEDULED",
  FAILED: "PROPAGATION_STATE_FAILED",
  ARCHIVED: "PROPAGATION_STATE_ARCHIVED",
} as const;

export type PropagationState =
  (typeof PROPAGATION_STATE)[keyof typeof PROPAGATION_STATE];

export const PROCESSING_STATE_COLLECTION_PROPAGATED =
  "PROCESSING_STATE_COLLECTION_PROPAGATED";

export const ITEM_STATUS_FAILED = "ITEM_STATUS_FAILED";

// ── List / envelope response schemas ──────────────────────────────────────────

export const ListPropagationsResponseSchema = z.object({
  propagations: z.array(PropagationSchema).default([]),
  total: z.number().default(0),
});
export type ListPropagationsResponse = z.infer<
  typeof ListPropagationsResponseSchema
>;

/** `PATCH .../items/{item_id}` returns the single updated item, wrapped. */
export const UpdatePropagationItemResponseSchema = z.object({
  item: PropagationItemSchema,
});
export type UpdatePropagationItemResponse = z.infer<
  typeof UpdatePropagationItemResponseSchema
>;

// ── Streamed progress objects ─────────────────────────────────────────────────

export const PropagateProgressSchema = z.object({
  total: z.number().default(0),
  completed: z.number().default(0),
  // The SKU/item that just resolved (absent on a bare status-only frame).
  item: PropagationItemSchema.optional(),
  // Loose enum (PropagationStatus) — overall status of the run.
  status: z.string().default(""),
});
export type PropagateProgress = z.infer<typeof PropagateProgressSchema>;

// Publish streams a flatter per-asset frame than propagate: instead of the
// resolved `item`, it reports the asset's slug + the product identity, plus a
// `failure_reason` when that asset fails to publish.
export const PublishProgressSchema = z.object({
  total: z.number().default(0),
  completed: z.number().default(0),
  asset_slug: z.string().default(""),
  status: z.string().default(""),
  failure_reason: z.string().default(""),
  vendor_product_id: z.string().default(""),
  vendor_variant_id: z.string().default(""),
});
export type PublishProgress = z.infer<typeof PublishProgressSchema>;

export const PUBLISH_STATUS = {
  UNSPECIFIED: "PUBLISH_STATUS_UNSPECIFIED",
  PUBLISHING: "PUBLISH_STATUS_PUBLISHING",
  PUBLISHED: "PUBLISH_STATUS_PUBLISHED",
  FAILED: "PUBLISH_STATUS_FAILED",
} as const;

// ── Request bodies ────────────────────────────────────────────────────────────

/**
 * `POST /v1/{site_id}/propagations` body. Initializes workflow state from a
 * seeds_map + a target collection. Matches the ticket's
 * `propagation_create_request` mock. Kept as a plain interface (not parsed) —
 * only responses are Zod-validated.
 */
export interface CreatePropagationRequest {
  seeds_map: SeedsMapInput;
  /** Display name; see `composePropagationName`. */
  name?: string;
  /** Target group to propagate across — handle + PRODUCT_GROUP_TYPE. */
  group_id?: string;
  group_type?: ProductGroupType;
  additional_prompt?: string;
  /** Where the workflow started, lowercase (e.g. "blueprint"). */
  started_from?: string;
  from_blueprint_id?: string;
  /** Initial workflow state — see {@link PROPAGATION_STATE}. */
  propagation_state?: string;
  pilot_vendor_product_id?: string;
  pilot_vendor_variant_id?: string;
  pilot_image_url?: string;
  blueprint_id?: string;
}

/**
 * `PATCH /v1/{site_id}/propagations/{slug}` body: the changed fields, flat.
 * The backend writes whatever is present, so there is no field mask.
 */
export type UpdatePropagationRequest = Partial<
  Pick<
    Propagation,
    | "status"
    | "step"
    | "name"
    | "additional_prompt"
    | "pilot_additional_prompt"
    | "pilot_image_url"
    | "pilot_vendor_product_id"
    | "blueprint_id"
    | "from_blueprint_id"
    | "group_id"
    | "group_type"
  >
> & {
  propagation_state?: PropagationState;
  seeds_map?: SeedsMapInput;
  items?: PropagationItemInput[];
  product_group?: ProductGroup;
};

/** `PATCH /v1/{site_id}/propagations/{slug}/items/{item_id}` body. */
export interface UpdatePropagationItemRequest {
  is_accepted: boolean;
}

/** proto `ProductRef` — one product in the collection being propagated across. */
export const ProductRefSchema = z.object({
  vendor_product_id: z.string().default(""),
  vendor_variant_id: z.string().default(""),
  image_url: z.string().default(""),
  title: z.string().default(""),
  description: z.string().optional(),
  category: z.string().optional(),
});
export type ProductRef = z.infer<typeof ProductRefSchema>;

export const ProductGroupSchema = z.object({
  group_type: ProductGroupTypeSchema.optional(),
  group_id: z.string().optional(),
  handle: z.string().optional(),
  products: z.array(ProductRefSchema).optional(),
});
export type ProductGroup = z.infer<typeof ProductGroupSchema>;

/**
 * `POST /v1/stream/propagate` body. Apply a seeds_map across a target
 * collection, OR drive an existing propagation (by `propagation_slug`). Parsed
 * in `streamPropagate` so omitting both targets fails at call time instead of as
 * a 400. The `product_group` carries the target group plus the explicit product
 * list to propagate across (proto `PropagateRequest.product_group`).
 */
export const PropagateRequestSchema = z
  .object({
    site_id: z.string(),
    seeds_map: SeedsMapSchema.optional(),
    target_collection_id: z.string().optional(),
    propagation_slug: z.string().optional(),
    additional_prompt: z.string().optional(),
    product_group: ProductGroupSchema.optional(),
    blueprint_id: z.string().optional(),
  })
  .refine(
    (req) =>
      Boolean(req.target_collection_id?.trim()) ||
      Boolean(req.propagation_slug?.trim()),
    {
      message:
        "PropagateRequest requires at least one of target_collection_id or propagation_slug",
    },
  );
export type PropagateRequest = z.infer<typeof PropagateRequestSchema>;

export interface PublishAssetInput {
  asset: {
    slug: string;
    url: string;
    media_type: string;
  };
  vendor_product_id?: string;
  vendor_variant_id?: string;
}

/** `POST /v1/stream/publish` body. */
export interface PublishRequest {
  site_id: string;
  publish_assets: PublishAssetInput[];
}
